import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import ReactStars from 'react-stars';
import { AutoSizer, List, CellMeasurer, CellMeasurerCache } from 'react-virtualized';
import { BsInfoCircle, BsChevronDown, BsChevronUp, BsClipboard2CheckFill } from 'react-icons/bs';
import { formatShortDate } from '../../../utils/helpers';
import { formatTime, resolveCategory, getIconBasedOnCat, CARD_DEFAULT_HEIGHT } from '../utils/reportHelpers';
import HtmlTooltip from './HtmlTooltip';
import RatingModal from './RatingModal';
import VettingReportButton from './VettingReportButton';

const ReportTableCard = ({
    data,
    featureFlags,
    onRatingSubmit,
    onSignoffModal,
    onQueryFormOpen,
    prevFilteredDataRef,
    updateLineItemRef,
}) => {
    const [showRatingFeedback, setShowRatingFeedback] = useState(false);
    const [tableData, setTableData] = useState(data);
    const [feedbackdata, setfeedbackdata] = useState({ rating: null, lineid: null, rl: null });
    const [notesOpen, setNotesOpen] = useState({});
    const ratingModalHide = () => setShowRatingFeedback(false);

    const ShowFeedbackmodel = (rating, lineId, Rl) => {
        setShowRatingFeedback(true);
        setfeedbackdata({ rating, lineid: lineId, rl: Rl });
    };

    function getWidthOfClass(className) {
        const elements = document.getElementsByClassName(className);
        if (elements.length === 0) return 0;
        const width = elements[0].getBoundingClientRect().width;
        return (width > 1920 || width < 569) ? width : 2320;
    }
    const widthOfDiv = getWidthOfClass('report-table-container');

    useEffect(() => {
        if (!tableData.length) {
            setTableData(data);
        } else {
            const previousData = prevFilteredDataRef.current || [];
            const updatedData = data.map(dataItem => {
                const existingItem = previousData.find(dd => dd.rw === dataItem.rw);
                return existingItem || dataItem;
            });
            setTableData(updatedData);
        }
    }, [data]);

    const ratingChanged = (newRating, lineId, rl) => {
        if (lineId !== 0) {
            setTableData(prevData => {
                const newData = prevData.map(item =>
                    item.lineId === lineId ? { ...item, rating: newRating } : item
                );
                prevFilteredDataRef.current = newData;
                return newData;
            });
            if (newRating === 1 || newRating === 5) {
                ShowFeedbackmodel(newRating, lineId, rl);
            } else if (newRating >= 2 && newRating <= 4) {
                onRatingSubmit(newRating, lineId, rl, null);
            }
        }
    };

    const handleLineItemUpdate = (lineid, ratings, signatures) => {
        if (lineid !== 0) {
            setTableData(prevData => {
                const newData = prevData.map(item =>
                    item.lineId === lineid ? { ...item, requireApproval: false, rating: ratings, managerSignature: signatures } : item
                );
                prevFilteredDataRef.current = newData;
                return newData;
            });
        }
    };
    updateLineItemRef.current = handleLineItemUpdate;

    const getStatusBorderClass = (rowData) => {
        if (rowData.requireApproval) return 'mobile-card-v2__inner--pending';
        if (rowData.managerSignature) return 'mobile-card-v2__inner--approved';
        return 'mobile-card-v2__inner--default';
    };

    const toggleNotes = (index) => {
        setNotesOpen(prev => ({ ...prev, [index]: !prev[index] }));
        cache.clear(index);
    };

    const [cache] = useState(() => new CellMeasurerCache({
        defaultHeight: CARD_DEFAULT_HEIGHT,
        fixedWidth: true
    }));

    function rowRenderer({ key, index, style, parent }) {
        const rowData = tableData[index];
        if (!rowData) return null;

        return (
            <CellMeasurer cache={cache} columnIndex={0} key={key} parent={parent} rowIndex={index}>
                {({ measure, registerChild }) => (
                    <div className="mobile-card-v2" key={key} style={style} ref={registerChild}>
                        <div className={`mobile-card-v2__inner ${getStatusBorderClass(rowData)}`} onLoad={measure}>
                            <div className="mobile-card-v2__header">
                                <div className="mobile-card-v2__header-left">
                                    <span className="mobile-card-v2__status-icon">{getIconBasedOnCat(resolveCategory(rowData.cat, rowData.rowHighlightColour, rowData.credited))}</span>
                                    {rowData.infoPanel && rowData.infoPanel.trim() !== '' && (
                                        <HtmlTooltip content={rowData.infoPanel}>
                                            <button className="mobile-card-v2__info-btn"><BsInfoCircle size={14} /></button>
                                        </HtmlTooltip>
                                    )}
                                </div>
                                <div className="mobile-card-v2__rating">
                                    {rowData.lineId !== 0 && (
                                        <ReactStars
                                            count={5}
                                            onChange={(nr) => ratingChanged(nr, rowData.lineId, rowData.rl)}
                                            value={rowData.rating}
                                            size={16}
                                            half={false}
                                            color2="#ffc107"
                                            color1="#555"
                                        />
                                    )}
                                </div>
                            </div>

                            <div className="mobile-card-v2__site">{rowData.site}</div>

                            <div className="mobile-card-v2__data">
                                <div className="mobile-card-v2__col">
                                    <div className="mobile-card-v2__field">
                                        <span className="mobile-card-v2__label">Date: </span>
                                        <span className="mobile-card-v2__value">{formatShortDate(rowData.jobdate)}</span>
                                    </div>
                                    <div className="mobile-card-v2__field">
                                        <span className="mobile-card-v2__label">Department: </span>
                                        <span className="mobile-card-v2__value">{rowData.department}</span>
                                    </div>
                                    <div className="mobile-card-v2__field">
                                        <span className="mobile-card-v2__label">Staff: </span>
                                        <span className="mobile-card-v2__value">{rowData.provider}</span>
                                    </div>
                                    <div className="mobile-card-v2__field">
                                        <span className="mobile-card-v2__label">Role: </span>
                                        <span className="mobile-card-v2__value">{rowData.role}</span>
                                    </div>
                                    <div className="mobile-card-v2__field">
                                        <span className="mobile-card-v2__label">LineID: </span>
                                        <span className="mobile-card-v2__value">{rowData.rw}</span>
                                    </div>
                                </div>
                                <div className="mobile-card-v2__col">
                                    <div className="mobile-card-v2__field">
                                        <span className="mobile-card-v2__label">Reported: </span>
                                        <span className="mobile-card-v2__value">{formatShortDate(rowData.reportedToFinance)}</span>
                                    </div>
                                    <div className="mobile-card-v2__field">
                                        <span className="mobile-card-v2__label">Received: </span>
                                        <span className="mobile-card-v2__value">{formatShortDate(rowData.invoiced)}</span>
                                    </div>
                                    <div className="mobile-card-v2__field">
                                        <span className="mobile-card-v2__label">Start: </span>
                                        <span className="mobile-card-v2__value">{formatTime(rowData.start)}</span>
                                    </div>
                                    <div className="mobile-card-v2__field">
                                        <span className="mobile-card-v2__label">Finish: </span>
                                        <span className="mobile-card-v2__value">{formatTime(rowData.finish)}</span>
                                    </div>
                                    <div className="mobile-card-v2__field">
                                        <span className="mobile-card-v2__label">Hours: </span>
                                        <span className="mobile-card-v2__value">{rowData.hours}</span>
                                    </div>
                                    <div className="mobile-card-v2__field">
                                        <span className="mobile-card-v2__label">Net Cost: </span>
                                        <span className="mobile-card-v2__value">&pound;{rowData.nettCost?.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}</span>
                                    </div>
                                </div>
                            </div>

                            {rowData.notes && (
                                <div className="mobile-card-v2__notes">
                                    <button className="mobile-card-v2__notes-toggle" onClick={() => toggleNotes(index)}>
                                        <span>Notes</span>
                                        {notesOpen[index] ? <BsChevronUp size={12} /> : <BsChevronDown size={12} />}
                                    </button>
                                    {notesOpen[index] && (
                                        <div className="mobile-card-v2__notes-content">{rowData.notes}</div>
                                    )}
                                </div>
                            )}

                            {rowData.managerSignature && (
                                <div className="mobile-card-v2__signature">
                                    <img src={rowData.managerSignature} alt="Manager signature" />
                                </div>
                            )}

                            <div className="mobile-card-v2__actions">
                                {featureFlags.copiaVettingDocument && (
                                    <VettingReportButton
                                        source={rowData.future ? 'costingFutureShift' : 'costingShift'}
                                        rowId={rowData.lineId}
                                        staffName={rowData.provider}
                                        className="mobile-card-v2__vetting-button"
                                    />
                                )}
                                {featureFlags.copiaQueryApprove && (
                                    <>
                                        {rowData.underQuery && (
                                            <span className="mobile-card-v2__btn mobile-card-v2__btn--challenge" style={{ color: rowData.underQueryColour }}>
                                                {rowData.underQuery}
                                            </span>
                                        )}
                                        {rowData.requireApproval ? (
                                            <button className="mobile-card-v2__btn mobile-card-v2__btn--approve" onClick={() => onSignoffModal(rowData)}>
                                                <BsClipboard2CheckFill className="me-1" /> Approve/Challenge
                                            </button>
                                        ) : rowData.challengeLink && !rowData.underQuery ? (
                                            <button className="mobile-card-v2__btn mobile-card-v2__btn--challenge" onClick={() => onQueryFormOpen(rowData)}>
                                                <BsClipboard2CheckFill className="me-1" /> Approve/Challenge
                                            </button>
                                        ) : null}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </CellMeasurer>
        );
    }

    return (
        <>
            <RatingModal showModal={showRatingFeedback} onHide={ratingModalHide} lineid={feedbackdata.lineid} onSubmit={onRatingSubmit} rating={feedbackdata.rating} rl={feedbackdata.rl} />
            <AutoSizer>
                {({ height }) => (
                    <List
                        width={widthOfDiv}
                        height={height}
                        rowCount={tableData.length}
                        deferredMeasurementCache={cache}
                        rowHeight={cache.rowHeight}
                        rowRenderer={rowRenderer}
                    />
                )}
            </AutoSizer>
        </>
    );
};

ReportTableCard.propTypes = {
    data: PropTypes.array.isRequired,
    featureFlags: PropTypes.object.isRequired,
    onRatingSubmit: PropTypes.func.isRequired,
    onSignoffModal: PropTypes.func.isRequired,
    onQueryFormOpen: PropTypes.func.isRequired,
    prevFilteredDataRef: PropTypes.object.isRequired,
    updateLineItemRef: PropTypes.object.isRequired,
};

export default ReportTableCard;
